import { Link } from 'react-router-dom'
import { Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { usePageTitle, setDocumentTitle } from '../utils/translation'

export default function Privacy() {
  const { t } = useTranslation()
  const pageTitle = usePageTitle('pages.privacy.title', 'Privacy Policy')
  
  useEffect(() => {
    setDocumentTitle(pageTitle)
  }, [pageTitle])

  // Helper function to render list items from translation array
  const renderListItems = (path: string) => {
    const items = t(path, { returnObjects: true })
    if (Array.isArray(items)) {
      return items.map((item: any, index: number) => (
        <li key={index} className="mb-1">{String(item)}</li>
      ))
    }
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm">
        <div className="flex items-center px-4 py-4">
          <Link 
            to="/" 
            className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-600 mr-4 shadow-sm hover:bg-amber-700 transition-colors"
            aria-label="Return to home"
          >
            <Home size={20} className="text-white" />
          </Link>
          <h1 className="font-bold text-xl text-amber-800">{t('pages.privacy.title')}</h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-5 py-8">
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-amber-900 mb-4">{t('pages.privacy.piika_privacy')}</h2>
          <p className="text-amber-800 mb-4">
            {t('pages.privacy.last_updated')}
          </p>
          
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('pages.privacy.sections.introduction.title')}</h3>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.introduction.content')}
            </p>
          </section>
          
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('pages.privacy.sections.information_collected.title')}</h3>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.information_collected.content')}
            </p>
            <ul className="list-disc pl-6 mb-3 text-amber-800">
              {renderListItems('pages.privacy.sections.information_collected.items')}
            </ul>
          </section>
          
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('pages.privacy.sections.information_usage.title')}</h3>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.information_usage.content')}
            </p>
            <ul className="list-disc pl-6 mb-3 text-amber-800">
              {renderListItems('pages.privacy.sections.information_usage.items')}
            </ul>
          </section>
          
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('pages.privacy.sections.information_sharing.title')}</h3>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.information_sharing.content')}
            </p>
            <ul className="list-disc pl-6 mb-3 text-amber-800">
              {renderListItems('pages.privacy.sections.information_sharing.items')}
            </ul>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.information_sharing.no_sell')}
            </p>
          </section>
          
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('pages.privacy.sections.data_security.title')}</h3>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.data_security.content')}
            </p>
          </section>
          
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('pages.privacy.sections.your_rights.title')}</h3>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.your_rights.content')}
            </p>
            <ul className="list-disc pl-6 mb-3 text-amber-800">
              {renderListItems('pages.privacy.sections.your_rights.items')}
            </ul>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.your_rights.exercise_rights')}
            </p>
          </section>
          
          <section className="mb-6">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('pages.privacy.sections.changes.title')}</h3>
            <p className="text-amber-800 mb-3">
              {t('pages.privacy.sections.changes.content')}
            </p>
          </section>
          
          <section>
            <h3 className="text-lg font-semibold text-amber-900 mb-2">{t('pages.privacy.sections.contact.title')}</h3>
            <p className="text-amber-800">
              {t('pages.privacy.sections.contact.content')}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white py-6 px-5 border-t border-amber-100">
        <div className="flex flex-col items-center space-y-4">
          <p className="text-center text-xs text-amber-600">
            &copy; {new Date().getFullYear()} {t('pages.privacy.footer.copyright')}
          </p>
          <div className="flex space-x-4">
            <a href="/terms" className="text-xs text-amber-700">{t('pages.privacy.footer.terms')}</a>
            <a href="/privacy" className="text-xs text-amber-700 font-bold">{t('pages.privacy.footer.privacy')}</a>
            <a href="/contact" className="text-xs text-amber-700">{t('pages.privacy.footer.contact')}</a>
          </div>
        </div>
      </footer>
    </div>
  )
} 